import { useState, useLayoutEffect, useRef } from 'react';

/**
 * Custom hook for handling container resizing with ResizeObserver
 *
 * This hook manages the responsive behavior of a container using ResizeObserver.
 * It's responsible for:
 * 1. Detecting size changes in the container (e.g., when dropdowns open/close)
 * 2. Updating the dimensions state to trigger re-renders
 * 3. Ensuring smooth performance during rapid size changes
 *
 * The hook uses a debounced approach to handle rapid size changes that can occur
 * when adding/removing maps or when dropdowns open/close. It also includes
 * error handling to prevent ResizeObserver loop errors.
 *
 * @param containerRef - React ref to the container element to observe
 * @returns A state object containing the current dimensions { width, height }
 */
export function useContainerResize(containerRef: React.RefObject<HTMLDivElement>) {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const timeoutRef = useRef<NodeJS.Timeout>();
  const observerRef = useRef<ResizeObserver | null>(null);
  const pendingUpdateRef = useRef<{ width: number; height: number } | null>(null);
  const isUpdatingRef = useRef(false);

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    // Clear any existing timeout and observer
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer with error handling
    try {
      observerRef.current = new ResizeObserver((entries) => {
        // If we're already processing an update, skip this one
        if (isUpdatingRef.current) return;

        // Cancel any pending updates
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Store the latest dimensions
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          pendingUpdateRef.current = { width, height };
        }

        // Debounce the size update to prevent rapid re-renders
        timeoutRef.current = setTimeout(() => {
          try {
            isUpdatingRef.current = true;
            if (pendingUpdateRef.current) {
              setDimensions(pendingUpdateRef.current);
              pendingUpdateRef.current = null;
            }
          } catch (error) {
            console.warn('Error in ResizeObserver callback:', error);
          } finally {
            isUpdatingRef.current = false;
          }
        }, 300); // Increased debounce time to 300ms for better stability
      });

      // Start observing with error handling
      try {
        observerRef.current.observe(containerRef.current);
      } catch (error) {
        console.warn('Error observing container:', error);
      }
    } catch (error) {
      console.warn('Error creating ResizeObserver:', error);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      isUpdatingRef.current = false;
      pendingUpdateRef.current = null;
    };
  }, [containerRef]);

  return dimensions;
}
